/**
 * Navigation TTI Tracker
 *
 * Tracks Time To Interactive for route changes (soft navigations).
 * Only tracks user-initiated navigations (click/keydown within 1s of pushState).
 *
 * How it works:
 * 1. Tracks user interactions (click/keydown) to detect user-initiated navigations
 * 2. Intercepts History API (pushState/popstate) to detect navigation start
 * 3. Uses PerformanceObserver (resource timing) to track ALL network requests
 * 4. TTI = time when last network request completed (relative to nav start)
 * 5. Reports after NETWORK_QUIET_MS quiet, confirmed by QUIET_WINDOW_MS
 *
 * Based on Google's TTI polyfill recommendations:
 * - 5 second quiet window for confirmation
 * - Only tracks user-initiated navigations
 */

import { shouldIgnoreForTTI } from "./networkIgnoreList";
import { shouldReportTTI } from "./samplingService";
import {
  NETWORK_QUIET_MS,
  QUIET_WINDOW_MS,
  MAX_TTI_WAIT_MS,
  USER_INTERACTION_WINDOW_MS,
  getTTIRating,
  reportTTI,
  createResourceObserver,
  getSessionId,
  getEffectiveNetworkType,
  type NetworkRequestInfo,
  type ResourceTimingEntry,
} from "./ttiHelpers";
import {
  initSoftNavigationTracking,
  isSoftNavigationSupported,
} from "./softNavigationHelper";

// ============================================================================
// Debug flag - set to true to enable verbose logging
// ============================================================================
const DEBUG = false;

const debugLog = (...args: unknown[]) => {
  if (DEBUG) console.log("[NavTTI]", ...args);
};

// ============================================================================
// State
// ============================================================================

let isTracking = false;
let navStartTime = 0; // performance.now() when navigation started
let navigationId = "";
let fromRoute = "";
let toRoute = "";

// Track the last resource completion time (relative to performance.timeOrigin)
let lastResourceEndTime = 0;

// All completed network requests for reporting
let networkRequests: NetworkRequestInfo[] = [];

// Observer
let resourceObserver: PerformanceObserver | null = null;

// Timers
let quietTimer: ReturnType<typeof setTimeout> | null = null;
let maxTimer: ReturnType<typeof setTimeout> | null = null;

// Flag to prevent double reporting
let reported = false;

// User interaction tracking - only track TTI for user-initiated navigations
let lastUserInteractionTime = 0;

// ============================================================================
// Public API
// ============================================================================

/**
 * Start tracking navigation TTI
 * @param from - The route being navigated from
 * @param to - The route being navigated to
 * @param useCurrentTime - If true, use performance.now() instead of last interaction time
 *                         (used for popstate where there's no DOM interaction event)
 */
export function startNavigationTTI(
  from: string,
  to: string,
  useCurrentTime: boolean = false
): void {
  // If already tracking, finalize the previous navigation first
  // This ensures we report the TTI even if user navigates before quiet period completes
  if (isTracking && !reported) {
    debugLog("New navigation started, finalizing previous TTI...");
    finalize();
  }

  cleanup();

  isTracking = true;

  // Determine the navigation start time:
  // 1. For popstate (back/forward), always use current time since there's no DOM interaction
  // 2. For clicks, use the interaction time if it's recent (within USER_INTERACTION_WINDOW_MS)
  // 3. Fall back to current time if interaction timestamp is stale
  const now = performance.now();
  const interactionAge = now - lastUserInteractionTime;
  const isRecentInteraction =
    lastUserInteractionTime > 0 && interactionAge <= USER_INTERACTION_WINDOW_MS;

  if (useCurrentTime) {
    navStartTime = now;
    debugLog("Using current time for popstate navigation");
  } else if (isRecentInteraction) {
    navStartTime = lastUserInteractionTime;
    debugLog(`Using interaction time (${Math.round(interactionAge)}ms ago)`);
  } else {
    navStartTime = now;
    debugLog(
      `Interaction too old (${Math.round(
        interactionAge
      )}ms), using current time`
    );
  }

  navigationId = `${Date.now()}_${getSessionId() || "unknown"}`;
  fromRoute = from;
  toRoute = to;
  lastResourceEndTime = navStartTime;
  networkRequests = [];
  reported = false;

  // Start resource observer - this captures ALL network activity with accurate timing
  resourceObserver = createResourceObserver(processResources);

  // Schedule quiet check
  scheduleQuietCheck();

  // Max timeout failsafe
  maxTimer = setTimeout(() => {
    debugLog("Max timeout reached, finalizing...");
    finalize();
  }, MAX_TTI_WAIT_MS);

  debugLog(`Started: ${from} -> ${to}`);
}

/**
 * Check if navigation is currently being tracked
 */
export function isNavigationTracking(): boolean {
  return isTracking;
}

/**
 * Called by callStackTracker when fetch/XHR activity occurs
 * We only use this to track in-flight state, not for TTI timing
 * (PerformanceObserver gives us accurate timing via responseEnd)
 */
export function onNetworkActivity(
  _url: string,
  _isStart: boolean = true,
  _bytesTransferred?: number,
  _initiatorType?: string
): void {
  // No-op: We rely solely on PerformanceObserver for timing accuracy
  // This function exists for backward compatibility with callStackTracker
}

// ============================================================================
// Internal Functions
// ============================================================================

// Buffer time to capture resources that started just before pushState
// (e.g., lazy-loaded Vue components triggered by click before route change)
const PRE_NAV_BUFFER_MS = 500;

/**
 * Process resource timing entries from PerformanceObserver
 */
function processResources(entries: ResourceTimingEntry[]): void {
  if (!isTracking) return;

  let hasNewActivity = false;

  for (const entry of entries) {
    // Skip ignored URLs
    if (shouldIgnoreForTTI(entry.name)) {
      debugLog(`Ignored: ${entry.name}`);
      continue;
    }

    // Skip resources that started well before our navigation
    // Allow 500ms buffer for resources triggered by click before pushState
    if (entry.startTime < navStartTime - PRE_NAV_BUFFER_MS) {
      debugLog(
        `Skipped (too early): ${entry.name} startTime=${Math.round(
          entry.startTime
        )} navStartTime=${Math.round(navStartTime)}`
      );
      continue;
    }

    // Track the latest completion time
    if (entry.responseEnd > lastResourceEndTime) {
      lastResourceEndTime = entry.responseEnd;
      hasNewActivity = true;
    }

    // Record request info for reporting
    const requestStartTime = Math.max(
      0,
      Math.round(entry.startTime - navStartTime)
    );
    const duration = Math.round(entry.duration);
    const bytesTransferred =
      entry.transferSize > 0
        ? entry.transferSize
        : entry.encodedBodySize > 0
        ? entry.encodedBodySize
        : undefined;

    networkRequests.push({
      url: sanitizeUrl(entry.name),
      startTime: requestStartTime,
      duration,
      bytesTransferred,
      initiatorType: entry.initiatorType,
    });

    debugLog(
      `Resource: ${entry.initiatorType} - ${duration}ms @ ${requestStartTime}ms`
    );
  }

  if (hasNewActivity) {
    // Reset quiet timer since we got new activity
    scheduleQuietCheck();
  }
}

/**
 * Schedule check for quiet period
 */
function scheduleQuietCheck(): void {
  if (quietTimer) clearTimeout(quietTimer);

  // Wait for NETWORK_QUIET_MS after last activity, then check
  const timeSinceLastActivity = performance.now() - lastResourceEndTime;
  const waitTime = Math.max(10, NETWORK_QUIET_MS - timeSinceLastActivity);

  quietTimer = setTimeout(checkQuiet, waitTime);
}

/**
 * Check if we've been quiet long enough
 */
function checkQuiet(): void {
  if (!isTracking || reported) return;

  const now = performance.now();
  const timeSinceLastActivity = now - lastResourceEndTime;

  if (timeSinceLastActivity >= NETWORK_QUIET_MS) {
    // Quiet period achieved - start final confirmation timer
    debugLog(
      `Quiet for ${Math.round(
        timeSinceLastActivity
      )}ms, waiting ${QUIET_WINDOW_MS}ms to confirm...`
    );
    quietTimer = setTimeout(finalize, QUIET_WINDOW_MS);
  } else {
    // Not quiet yet, check again
    scheduleQuietCheck();
  }
}

/**
 * Finalize and report TTI
 */
function finalize(): void {
  if (!isTracking || reported) return;
  reported = true;

  // TTI = time from navigation start to last resource completion
  const tti = Math.round(lastResourceEndTime - navStartTime);
  const rating = getTTIRating(tti);

  debugLog(`Final TTI: ${tti}ms (${networkRequests.length} requests)`);

  // Check sampling before reporting
  if (!shouldReportTTI(rating)) {
    debugLog(`TTI not reported (sampling: rating=${rating})`);
    cleanup();
    return;
  }

  reportTTI({
    type: "TTI",
    navigationId,
    duration: tti,
    rating,
    fromRoute,
    toRoute,
    longTaskCount: 0, // Simplified - not tracking long tasks
    totalBlockingTime: 0, // Simplified - not tracking TBT
    networkRequestCount: networkRequests.length,
    networkRequests,
    effectiveNetworkType: getEffectiveNetworkType(),
    method: "navigation",
  });

  cleanup();
}

/**
 * Cleanup all state and observers
 */
function cleanup(): void {
  isTracking = false;

  if (resourceObserver) {
    resourceObserver.disconnect();
    resourceObserver = null;
  }
  if (quietTimer) {
    clearTimeout(quietTimer);
    quietTimer = null;
  }
  if (maxTimer) {
    clearTimeout(maxTimer);
    maxTimer = null;
  }
}

/**
 * Sanitize URL - redact sensitive query params
 */
function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url, window.location.origin);
    if (!urlObj.search) return urlObj.origin + urlObj.pathname;

    const SENSITIVE = [
      "token",
      "key",
      "api_key",
      "password",
      "secret",
      "auth",
      "session",
      "access_token",
    ];
    const params: string[] = [];

    urlObj.searchParams.forEach((value, key) => {
      const isSensitive = SENSITIVE.some((s) => key.toLowerCase().includes(s));
      params.push(`${key}=${isSensitive ? "[REDACTED]" : value}`);
    });

    return urlObj.origin + urlObj.pathname + "?" + params.join("&");
  } catch {
    return url;
  }
}

// ============================================================================
// User Interaction Tracking
// ============================================================================

/**
 * Check if navigation was initiated by user interaction (click/keydown)
 */
function isUserInitiatedNavigation(): boolean {
  const timeSinceInteraction = performance.now() - lastUserInteractionTime;
  return timeSinceInteraction <= USER_INTERACTION_WINDOW_MS;
}

/**
 * Setup user interaction tracking (click/keydown)
 *
 * Note: These listeners are intentionally never removed because:
 * 1. TTI tracking runs for the entire session lifetime
 * 2. The callback is minimal (just updates a timestamp)
 * 3. Memory footprint is negligible
 */
function setupUserInteractionTracking(): void {
  const updateInteractionTime = (event: Event) => {
    // Use event.timeStamp for accurate timing (relative to performance.timeOrigin)
    lastUserInteractionTime = event.timeStamp;
  };

  // Track clicks and keyboard navigation
  // eslint-disable-next-line listeners/no-missing-remove-event-listener
  window.addEventListener("click", updateInteractionTime, { capture: true });
  // eslint-disable-next-line listeners/no-missing-remove-event-listener
  window.addEventListener("keydown", updateInteractionTime, { capture: true });
}

// ============================================================================
// History API Interception
// ============================================================================

let initialized = false;

/**
 * Initialize navigation TTI tracking
 */
export function initNavigationTTI(): void {
  if (typeof window === "undefined" || initialized) return;
  initialized = true;

  // Setup user interaction tracking first
  setupUserInteractionTracking();

  const originalPushState = history.pushState.bind(history);
  let lastPath = window.location.pathname + window.location.search;

  history.pushState = function (
    data: unknown,
    unused: string,
    url?: string | URL | null
  ) {
    const fromPath = lastPath;
    const result = originalPushState(data, unused, url);
    const toPath = extractPath(url);
    lastPath = toPath;

    // Only track TTI if route changed AND user initiated the navigation
    if (fromPath !== toPath && isUserInitiatedNavigation()) {
      // startNavigationTTI will finalize any in-progress TTI before starting new one
      startNavigationTTI(fromPath, toPath);
    } else if (fromPath !== toPath) {
      debugLog(`Skipped (not user-initiated): ${fromPath} -> ${toPath}`);
    }

    return result;
  };

  // Handle browser back/forward
  // Note: popstate doesn't always have a corresponding click/keydown event
  // (e.g., browser back button, swipe gestures), so we pass useCurrentTime=true
  // to avoid using stale interaction timestamps
  // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener
  window.addEventListener("popstate", () => {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== lastPath) {
      // startNavigationTTI will finalize any in-progress TTI before starting new one
      // Use current time since popstate may not have a recent DOM interaction
      startNavigationTTI(lastPath, currentPath, true);
      lastPath = currentPath;
    }
  });

  debugLog("Initialized with user interaction tracking");

  // Initialize Chrome's Soft Navigation API if supported (Chrome 139+)
  if (isSoftNavigationSupported()) {
    initSoftNavigationTracking();
    debugLog("Soft Navigation API tracking enabled");
  }
}

function extractPath(url?: string | URL | null): string {
  if (!url) return window.location.pathname + window.location.search;
  try {
    const parsed = new URL(url.toString(), window.location.origin);
    return parsed.pathname + parsed.search;
  } catch {
    return url.toString();
  }
}
